/**
 * Runtime platform detection helpers.
 *
 * `@capacitor/core` is always installed, so `Capacitor.isNativePlatform()` is
 * the source of truth. We import it lazily so that Vite's web build doesn't
 * include native plugin shims it doesn't need.
 */
import { Capacitor } from "@capacitor/core";

/** True when running inside a Capacitor iOS / Android shell. */
export function isNativePlatform(): boolean {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
}

/** "ios" | "android" | "web" */
export function getPlatform(): "ios" | "android" | "web" {
  try {
    const p = Capacitor.getPlatform();
    if (p === "ios" || p === "android") return p;
    return "web";
  } catch {
    return "web";
  }
}

/**
 * True inside the iOS native app shell. Used to gate off in-app
 * purchases of the Acer membership — Apple App Store Guideline 3.1.1
 * requires digital-feature subscriptions to use IAP, so on iOS we hide
 * the "Become an Acer" CTA (or route it to an external Safari page) and
 * let the user complete the purchase on acework.app. Web + Android are
 * unaffected.
 */
export function isIOSNative(): boolean {
  return isNativePlatform() && getPlatform() === "ios";
}
